import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";

const AuthContext = createContext(null);

const AUTH_USERNAME_DOMAIN = process.env.REACT_APP_AUTH_USERNAME_DOMAIN || "cleo.local";

const toAuthEmail = (username) => {
  const cleaned = username.trim().toLowerCase();
  if (cleaned.includes("@")) {
    return cleaned;
  }
  return `${cleaned}@${AUTH_USERNAME_DOMAIN}`;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [postLoginPath, setPostLoginPath] = useState("/portal");

  const loadProfile = async (userId) => {
    if (!userId) {
      setProfile(null);
      return;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("id,username,role")
      .eq("id", userId)
      .maybeSingle();

    if (error) {
      setProfile(null);
      return;
    }

    setProfile(data || null);
  };

  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      const { data } = await supabase.auth.getSession();
      if (!isMounted) {
        return;
      }

      const nextSession = data?.session || null;
      setSession(nextSession);
      setUser(nextSession?.user || null);
      await loadProfile(nextSession?.user?.id);
      setLoading(false);
    };

    bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user || null);
      await loadProfile(nextSession?.user?.id);
      setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (user) {
      setIsLoginModalOpen(false);
    }
  }, [user]);

  const signInWithUsername = useCallback(async (username, password) => {
    const email = toAuthEmail(username);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error, email };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  }, []);

  const openLoginModal = useCallback((path = "/portal") => {
    setPostLoginPath(path);
    setIsLoginModalOpen(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setIsLoginModalOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      session,
      user,
      profile,
      loading,
      isAuthenticated: !!user,
      isAdmin: profile?.role === "admin",
      signInWithUsername,
      signOut,
      isLoginModalOpen,
      openLoginModal,
      closeLoginModal,
      postLoginPath,
      usernameDomain: AUTH_USERNAME_DOMAIN,
    }),
    [
      session,
      user,
      profile,
      loading,
      isLoginModalOpen,
      openLoginModal,
      closeLoginModal,
      signInWithUsername,
      signOut,
      postLoginPath,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
};
